import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export interface VoiceQuery {
  text: string;
  context?: any;
}

export interface VoiceResponse {
  text: string;
  data?: any;
  action?: string;
  confidence: number;
}

class VoiceService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  async processQuery(query: string): Promise<VoiceResponse> {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/voice/query`,
        { query },
        { headers: this.getAuthHeaders() }
      );
      return response.data;
    } catch (error) {
      console.error('Error processing voice query:', error);
      return this.getMockResponse(query);
    }
  }

  async getVoiceCommands(): Promise<any> {
    try {
      const response = await axios.get(`${API_BASE_URL}/voice/commands`, {
        headers: this.getAuthHeaders(),
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching voice commands:', error);
      return this.getMockCommands();
    }
  }

  private getMockResponse(query: string): VoiceResponse {
    const queryLower = query.toLowerCase();

    // Revenue queries
    if (queryLower.includes('revenue')) {
      return {
        text: "Today's revenue is $8,750, which is 12% higher than yesterday. Temple location has generated $4,250 and Woodway has generated $4,500.",
        data: {
          metrics: {
            todayRevenue: 8750,
            yesterdayRevenue: 7812,
            templeRevenue: 4250,
            woodwayRevenue: 4500
          },
          chart: true
        },
        confidence: 0.95
      };
    }

    // Inventory queries
    if (queryLower.includes('inventory') || queryLower.includes('parts')) {
      return {
        text: "Current parts inventory shows 3 items running low: oil filters (12 units), brake pads (8 sets), and air filters (15 units). I recommend placing orders today.",
        data: {
          lowInventory: [
            { item: 'Oil Filters', count: 12, reorderPoint: 20 },
            { item: 'Brake Pads', count: 8, reorderPoint: 15 },
            { item: 'Air Filters', count: 15, reorderPoint: 25 }
          ]
        },
        confidence: 0.92
      };
    }

    // Staff productivity
    if (queryLower.includes('staff') || queryLower.includes('productivity')) {
      return {
        text: "Staff productivity is at 87.5% today, with an average of 2.3 jobs completed per technician. John Smith is your top performer with 4 jobs completed.",
        data: {
          productivity: 87.5,
          avgJobsPerTech: 2.3,
          topPerformer: {
            name: 'John Smith',
            jobsCompleted: 4
          }
        },
        confidence: 0.88
      };
    }

    // KPI reports
    if (queryLower.includes('kpi') || queryLower.includes('report')) {
      return {
        text: "This week's KPIs are strong. Revenue is up 15.2%, customer satisfaction is at 94.5%, and average service time has decreased to 45 minutes. Would you like me to generate a detailed report?",
        data: {
          kpis: {
            revenue: { value: 42500, change: 15.2 },
            satisfaction: { value: 94.5, change: 1.2 },
            serviceTime: { value: 45, change: -5.2 }
          }
        },
        action: 'showKPIDetails',
        confidence: 0.91
      };
    }

    // Default response
    return {
      text: "I can help you with revenue reports, inventory status, staff productivity, and KPI analysis. What would you like to know?",
      confidence: 0.75
    };
  }

  private getMockCommands(): any {
    return {
      categories: [
        {
          name: 'Revenue & Financial',
          commands: [
            "What's today's revenue?",
            "Show me this week's sales",
            "Compare revenue between locations",
            "What's our profit margin?"
          ]
        },
        {
          name: 'Inventory Management',
          commands: [
            "Check parts inventory",
            "What items are running low?",
            "Show inventory turnover",
            "Create purchase order"
          ]
        },
        {
          name: 'Staff & Productivity',
          commands: [
            "How is staff productivity?",
            "Show technician performance",
            "Check staff schedules",
            "Who's working today?"
          ]
        },
        {
          name: 'Reports & Analytics',
          commands: [
            "Generate weekly KPI report",
            "Show customer satisfaction trends",
            "Export monthly summary",
            "Predict next month's revenue"
          ]
        }
      ]
    };
  }
}

export const voiceService = new VoiceService();